import { reportContractClientError } from "@spark/frontend-config";
import { ContractClient } from "@spark/web";
import { BffContract } from "../../contract.ts";
import type {
	DocumentProgress,
	EmbedCode,
	StoredDocumentProgress,
	UserSettings,
	UserSettingsPatch,
} from "../../domain/index.ts";

const client = ContractClient(BffContract, {
	baseUrl: window.location.origin,
	onError: reportContractClientError,
});

/**
 * Exchanges the code from a short URL for a Readiant embed.
 * @param code The short URL code to fetch the embed code for.
 */
export function createEmbedCode(code: string): Promise<EmbedCode> {
	return client.bff.createEmbedCode({ code }).then(({ body }) => body);
}

/**
 * Gets the reading preferences of the logged in user.
 */
export function getUserSettings(): Promise<UserSettings | null> {
	return client.bff.getUserSettings().then(({ body }) => body.userSettings);
}

/**
 * Saves the reading preferences of the logged in user.
 * @param userSettings The settings to change; `null` clears a setting.
 */
export function saveUserSettings(
	userSettings: UserSettingsPatch,
): Promise<UserSettings | null> {
	return client.bff
		.updateUserSettings({ userSettings })
		.then((response) =>
			response.status === 200 ? response.body.userSettings : null,
		);
}

/**
 * Fetches the progress of a document for the logged in user.
 * @param documentId The Readiant ID of the document to fetch progress for.
 */
export function getDocumentProgress(
	documentId: string,
): Promise<DocumentProgress> {
	return client.bff
		.getDocumentProgress({ documentId })
		.then(({ body }) => body);
}

/**
 * Saves the progress of a document for the logged in user.
 * @param documentId The Readiant ID of the document to save progress for.
 * @param page The page to save for the document.
 */
export function saveDocumentProgress(
	documentId: string,
	page: number,
): Promise<StoredDocumentProgress | null> {
	return client.bff
		.updateDocumentProgress({ path: { documentId }, body: { page } })
		.then((response) => (response.status === 200 ? response.body : null));
}
